export const M6_UNIT_14 = {
  id: "m6-u14-numbers-times-dates",
  title: "Unidad 14: Numbers, Times, Dates & Statistics",
  topic: "Master fractions, decimals, percentages, telling time, and dates! Essential for understanding sports statistics, schedules, appointments, and news worldwide! 🕐📊",
  iconType: "clock",
  grammarImage: "grammar/grammar-m6-u14.jpg",
  grammarText: "Say decimals digit by digit: 3.97 = 'three point nine seven'. Use 'half past' and 'quarter to' for times. Know US vs UK date formats!",
  
  sets: [
    // ============================================
    // SETS 1-2: FRACTIONS & BASIC NUMBERS
    // ============================================
    {
      id: "m6-u14-s1",
      title: "Set 1: Fractions - Basic",
      category: "vocabulary",
      exercises: [
        {
          type: "mc",
          q: "How do you say 1/2?",
          options: ["One two", "A half", "One second"],
          a: "A half",
          tip: "1/2 = A HALF or ONE HALF! Special word in English!",
          successtip: "✓ A HALF! ½ Universal symbol! Half pizza, half hour, half price worldwide!"
        },
        {
          type: "mc",
          q: "How do you say 1/4?",
          options: ["One four", "A quarter", "One fourth"],
          a: "A quarter",
          tip: "1/4 = A QUARTER! Also one fourth in US English!",
          successtip: "✓ A QUARTER! ¼ Quarter of an hour = 15 minutes! Quarter is 25 cents in US!"
        },
        {
          type: "input",
          q: "How do you say 1/3? Write it: a ___",
          a: ["third", "Third"],
          tip: "1/3 = A THIRD! Use ordinal number (third, not three)!",
          successtip: "✓ A THIRD! ⅓ Bottom number uses ORDINAL: third, fourth, fifth! Pattern for all fractions!"
        },
        {
          type: "order",
          q: "Traduce: Tres cuartos (3/4)",
          words: ["three", "quarters"],
          a: "three quarters",
          tip: "Top = cardinal (three), bottom = ordinal plural (quarters)!",
          successtip: "✓ THREE QUARTERS! ¾ Top uses regular numbers, bottom uses ordinals! Simple pattern!"
        },
        {
          type: "mc",
          q: "How do you say 2/5?",
          options: ["Two five", "Two fifths", "Two fifth"],
          a: "Two fifths",
          tip: "2/5 = TWO FIFTHS! Bottom is plural when top is more than 1!",
          successtip: "✓ TWO FIFTHS! Top number 2+ makes bottom plural! Three fifths, four sixths!"
        },
        {
          type: "input",
          q: "Complete: 1½ = one and a ___",
          a: ["half", "Half"],
          tip: "Use AND to connect whole number and fraction!",
          successtip: "✓ ONE AND A HALF! Use AND between! Two and a half, three and a quarter!"
        },
        {
          type: "mc",
          q: "ERROR COMÚN: The five and half. ¿Correcto?",
          options: ["Yes, correct", "No, five and a half", "No, the five and a half"],
          a: "No, five and a half",
          tip: "No THE before numbers! FIVE and A half!",
          successtip: "✓ FIVE AND A HALF! No article THE before numbers in English!"
        },
        {
          type: "match",
          q: "Match the fraction to the words:",
          pairs: [
            { left: "1/2", right: "a half" },
            { left: "1/4", right: "a quarter" },
            { left: "1/3", right: "a third" }
          ],
          a: "completed",
          successtip: "✓ COMMON FRACTIONS! These three most used worldwide! Half, quarter, third essential!"
        },
        {
          type: "order",
          q: "Traduce: Dos tercios de la población (2/3)",
          words: ["two", "thirds", "of", "the", "population"],
          a: "two thirds of the population",
          tip: "TWO THIRDS! Ordinal plural + OF for portions!",
          successtip: "✓ TWO THIRDS! ⅔ Used in statistics, surveys, news reports worldwide!"
        },
        {
          type: "input",
          q: "How do you say 3/5? ___ ___",
          a: ["three fifths", "Three fifths", "three Fifths"],
          tip: "3 (cardinal) + fifths (ordinal plural)!",
          successtip: "✓ THREE FIFTHS! ⅗ Pattern consistent! Four sevenths, five eighths all follow same rule!"
        }
      ]
    },
    
    {
      id: "m6-u14-s2",
      title: "Set 2: Fractions in Context",
      category: "vocabulary",
      exercises: [
        {
          type: "mc",
          q: "The stadium was ¾ full. How do you say this?",
          options: ["Three four full", "Three quarters full", "Three fourths full"],
          a: "Three quarters full",
          tip: "THREE QUARTERS full! Most common in UK English!",
          successtip: "✓ THREE QUARTERS! Both 'three quarters' and 'three fourths' correct! Regional preference!"
        },
        {
          type: "order",
          q: "Traduce: Una cuarta parte de las personas",
          words: ["a", "quarter", "of", "the", "people"],
          a: "a quarter of the people",
          tip: "A QUARTER OF = una cuarta parte de!",
          successtip: "✓ A QUARTER OF! Useful for statistics and proportions in all contexts!"
        },
        {
          type: "input",
          q: "Complete: The game lasted 2½ hours = two and a ___ hours",
          a: ["half", "Half"],
          tip: "Two AND A half! Connect with AND!",
          successtip: "✓ TWO AND A HALF HOURS! Common duration! Movies, games, flights often this length!"
        },
        {
          type: "mc",
          q: "Half of the students passed. How much is 'half'?",
          options: ["1/3", "1/2", "2/3"],
          a: "1/2",
          tip: "HALF = 1/2 = 50%! All mean the same!",
          successtip: "✓ HALF = ½ = 50%! Different ways to express same amount! Interchangeable!"
        },
        {
          type: "match",
          q: "Match the fraction to its approximate percentage:",
          pairs: [
            { left: "1/2", right: "50%" },
            { left: "1/4", right: "25%" },
            { left: "3/4", right: "75%" }
          ],
          a: "completed",
          successtip: "✓ CONVERSION! Fractions ↔ Percentages! Useful for understanding statistics worldwide!"
        },
        {
          type: "order",
          q: "Traduce: Una tercera parte de la ciudad",
          words: ["a", "third", "of", "the", "city"],
          a: "a third of the city",
          tip: "A THIRD OF! One third = 33%!",
          successtip: "✓ A THIRD! About 33%! Common in news, reports, statistics globally!"
        },
        {
          type: "mc",
          q: "I ate ⅔ of the pizza. How do you say this?",
          options: ["Two three of", "Two thirds of", "Two third of"],
          a: "Two thirds of",
          tip: "TWO THIRDS! Plural because top number is 2!",
          successtip: "✓ TWO THIRDS! Most of the pizza! 66% approximately! Hungry day!"
        },
        {
          type: "input",
          q: "Complete: 1¼ kilometers = one and a ___ kilometers",
          a: ["quarter", "Quarter"],
          tip: "One AND A quarter! 1.25 km!",
          successtip: "✓ ONE AND A QUARTER! 1.25! Common in measurements, distances, weights!"
        },
        {
          type: "mc",
          q: "The bottle is ½ empty. What does this mean?",
          options: ["Completely empty", "Half empty", "Almost full"],
          a: "Half empty",
          tip: "HALF empty = 50% empty! Also 'half full' means same!",
          successtip: "✓ HALF EMPTY! Same as half full! Optimist vs pessimist view! Both correct!"
        },
        {
          type: "input",
          q: "How do you say 4/5? ___ ___",
          a: ["four fifths", "Four fifths", "four Fifths"],
          tip: "FOUR FIFTHS! 80% approximately!",
          successtip: "✓ FOUR FIFTHS! High proportion! Used for majority statistics in reports!"
        }
      ]
    },

    // ============================================
    // SETS 3-4: DECIMALS & PERCENTAGES
    // ============================================
    {
      id: "m6-u14-s3",
      title: "Set 3: Decimals",
      category: "vocabulary",
      exercises: [
        {
          type: "mc",
          q: "How do you say 0.5?",
          options: ["Zero five", "Nought point five", "Zero comma five"],
          a: "Nought point five",
          tip: "Use POINT not comma! Zero/nought/oh point five all correct!",
          successtip: "✓ POINT! . = POINT in English! 0.5 = zero/nought/oh point five! Three ways to start!"
        },
        {
          type: "mc",
          q: "How do you say 3.97?",
          options: ["Three point ninety-seven", "Three point nine seven", "Three comma nine seven"],
          a: "Three point nine seven",
          tip: "Say each digit SEPARATELY after point! Not 'ninety-seven'!",
          successtip: "✓ SEPARATE DIGITS! Each number after point said alone! 3.97 = three point nine seven!"
        },
        {
          type: "order",
          q: "Traduce: 1.7 (one point seven)",
          words: ["one", "point", "seven"],
          a: "one point seven",
          tip: "POINT = decimal point! 1.7 simple!",
          successtip: "✓ ONE POINT SEVEN! Simple pattern! 2.3, 4.8, 9.1 all same pattern!"
        },
        {
          type: "input",
          q: "How do you say 2.9? two ___ ___",
          a: ["point nine", "Point nine", "point Nine"],
          tip: "Two POINT nine! Each digit separate!",
          successtip: "✓ TWO POINT NINE! Used in temperatures, measurements, statistics everywhere!"
        },
        {
          type: "mc",
          q: "ERROR COMÚN: 5.25 = five point twenty-five. ¿Correcto?",
          options: ["Yes, correct", "No, five point two five", "No, five comma two five"],
          a: "No, five point two five",
          tip: "Each digit SEPARATE! Five point two five NOT twenty-five!",
          successtip: "✓ SEPARATE! 5.25 = five point two five! Each digit after point separate!"
        },
        {
          type: "match",
          q: "Match the decimal to how you say it:",
          pairs: [
            { left: "0.5", right: "zero point five" },
            { left: "1.3", right: "one point three" },
            { left: "4.89", right: "four point eight nine" }
          ],
          a: "completed",
          successtip: "✓ DECIMALS! Universal pattern! Used in prices, measurements, scores worldwide!"
        },
        {
          type: "order",
          q: "Traduce: 8.6 metros (say the number)",
          words: ["eight", "point", "six", "meters"],
          a: "eight point six meters",
          tip: "EIGHT point six meters! Distance measurement!",
          successtip: "✓ EIGHT POINT SIX METERS! Used in sports records, long jump, high jump globally!"
        },
        {
          type: "input",
          q: "How do you say 0.75? ___ point ___ ___",
          a: ["zero point seven five", "nought point seven five", "oh point seven five"],
          tip: "Three ways to say 0! Zero, nought, or oh!",
          successtip: "✓ THREE OPTIONS! Zero/nought/oh all correct! Regional and personal preference!"
        },
        {
          type: "mc",
          q: "The temperature is 10.2°C. How do you say this?",
          options: ["Ten comma two", "Ten point two", "Ten point twenty"],
          a: "Ten point two",
          tip: "TEN point TWO! Temperatures use decimals!",
          successtip: "✓ TEN POINT TWO! Temperature readings worldwide use decimals! Celsius or Fahrenheit!"
        },
        {
          type: "input",
          q: "Complete: 99.9% = ninety-nine point ___ percent",
          a: ["nine", "Nine"],
          tip: "Ninety-nine point NINE! Almost 100%!",
          successtip: "✓ NINETY-NINE POINT NINE! Very close to perfect! Used in purity, accuracy claims!"
        }
      ]
    },

    {
      id: "m6-u14-s4",
      title: "Set 4: Percentages",
      category: "vocabulary",
      exercises: [
        {
          type: "mc",
          q: "How do you say 55%?",
          options: ["Fifty-five percentage", "Fifty-five percent", "Fifty-five per hundred"],
          a: "Fifty-five percent",
          tip: "% = PERCENT! Say the number + percent!",
          successtip: "✓ PERCENT! % symbol universal! Used in statistics, discounts, surveys worldwide!"
        },
        {
          type: "order",
          q: "Traduce: 1% (one percent)",
          words: ["one", "percent"],
          a: "one percent",
          tip: "ONE percent! Very small amount!",
          successtip: "✓ ONE PERCENT! 1%! Small but significant in large numbers! 1% of a million = 10,000!"
        },
        {
          type: "input",
          q: "How do you say 99%? ninety-nine ___",
          a: ["percent", "Percent"],
          tip: "Ninety-nine PERCENT! Almost all!",
          successtip: "✓ NINETY-NINE PERCENT! 99%! Nearly complete! Used for success rates, satisfaction!"
        },
        {
          type: "mc",
          q: "How do you say 55.5%?",
          options: ["Fifty-five point five percent", "Fifty-five comma five percent", "Fifty-five five percent"],
          a: "Fifty-five point five percent",
          tip: "Combine decimal + percent! Point five!",
          successtip: "✓ DECIMALS + PERCENT! 55.5%! Precise statistics use decimal percentages!"
        },
        {
          type: "match",
          q: "Match the percentage to the fraction:",
          pairs: [
            { left: "50%", right: "1/2" },
            { left: "25%", right: "1/4" },
            { left: "75%", right: "3/4" }
          ],
          a: "completed",
          successtip: "✓ EQUIVALENTS! Different ways to express same proportion! Interchangeable in use!"
        },
        {
          type: "order",
          q: "Traduce: El 30% de las personas",
          words: ["thirty", "percent", "of", "the", "people"],
          a: "thirty percent of the people",
          tip: "THIRTY percent OF! About 1/3 of people!",
          successtip: "✓ THIRTY PERCENT! Common in surveys, polls, demographics worldwide!"
        },
        {
          type: "mc",
          q: "The discount is 20%. How do you say this?",
          options: ["Twenty per cent", "Twenty percent", "Both are correct"],
          a: "Both are correct",
          tip: "PERCENT or PER CENT both accepted! US vs UK spelling!",
          successtip: "✓ BOTH CORRECT! US: percent (one word), UK: per cent (two words)! Same meaning!"
        },
        {
          type: "input",
          q: "Complete: 100% = one ___ percent",
          a: ["hundred", "Hundred"],
          tip: "One HUNDRED percent! Complete, total!",
          successtip: "✓ ONE HUNDRED PERCENT! 100%! Complete success! Also used for emphasis: I'm 100% sure!"
        },
        {
          type: "mc",
          q: "ERROR COMÚN: The 75 percentage of students. ¿Correcto?",
          options: ["Yes, correct", "No, 75 percent of students", "No, the 75 percent of students"],
          a: "No, 75 percent of students",
          tip: "No THE! Use PERCENT not percentage! 75 percent!",
          successtip: "✓ NO THE! No article before percentages! 75 percent NOT the 75 percentage!"
        },
        {
          type: "input",
          q: "How do you say 82%? eighty-___ percent",
          a: ["two", "Two"],
          tip: "Eighty-TWO percent! 82%!",
          successtip: "✓ EIGHTY-TWO PERCENT! High percentage! Used for approval ratings, test scores!"
        }
      ]
    },

    // ============================================
    // SETS 5-6: TELLING TIME - 12-HOUR CLOCK
    // ============================================
    {
      id: "m6-u14-s5",
      title: "Set 5: Telling Time - Basic",
      category: "vocabulary",
      exercises: [
        {
          type: "mc",
          q: "How do you say 9:30?",
          options: ["Nine and thirty", "Nine thirty", "Nine hours thirty"],
          a: "Nine thirty",
          tip: "NINE THIRTY! Simple, direct! Also 'half past nine'!",
          successtip: "✓ NINE THIRTY! Two ways: 9:30 = nine thirty OR half past nine! Both common!"
        },
        {
          type: "mc",
          q: "Another way to say 9:30?",
          options: ["Half past nine", "Half to nine", "Half nine"],
          a: "Half past nine",
          tip: "HALF PAST nine! 30 minutes after 9!",
          successtip: "✓ HALF PAST! Classic way to say :30! Half past = 30 minutes after the hour!"
        },
        {
          type: "order",
          q: "Traduce: 3:45 (quarter to four)",
          words: ["quarter", "to", "four"],
          a: "quarter to four",
          tip: "QUARTER TO four! 15 minutes before 4!",
          successtip: "✓ QUARTER TO! 3:45 = 15 minutes to 4:00! Also 'three forty-five' works!"
        },
        {
          type: "input",
          q: "How do you say 2:15? ___ past ___",
          a: ["quarter past two", "Quarter past two", "a quarter past two", "A quarter past two"],
          tip: "QUARTER PAST two! 15 minutes after 2!",
          successtip: "✓ QUARTER PAST! 2:15 = quarter past two! 15 minutes after the hour!"
        },
        {
          type: "mc",
          q: "How do you say 7:00?",
          options: ["Seven hours", "Seven o'clock", "Seven zero zero"],
          a: "Seven o'clock",
          tip: "SEVEN O'CLOCK! Use o'clock for exact hours!",
          successtip: "✓ O'CLOCK! Only use for exact hours: 7:00, 8:00, 12:00! Not for 7:15!"
        },
        {
          type: "match",
          q: "Match the time to the phrase:",
          pairs: [
            { left: "6:30", right: "half past six" },
            { left: "8:15", right: "quarter past eight" },
            { left: "10:45", right: "quarter to eleven" }
          ],
          a: "completed",
          successtip: "✓ TIME PHRASES! Traditional British way! Still common worldwide in spoken English!"
        },
        {
          type: "order",
          q: "Traduce: Son las 5 en punto (5:00)",
          words: ["It's", "five", "o'clock"],
          a: "It's five o'clock",
          tip: "IT'S five o'clock! Use IT'S for time!",
          successtip: "✓ IT'S! Always IT'S for time in English! Not 'they are' like Spanish 'son las'!"
        },
        {
          type: "mc",
          q: "ERROR COMÚN: It's the three o'clock. ¿Correcto?",
          options: ["Yes, correct", "No, It's three o'clock", "No, They're three o'clock"],
          a: "No, It's three o'clock",
          tip: "No THE! IT'S three o'clock! No article with time!",
          successtip: "✓ NO THE! No article before time! It's three o'clock NOT the three o'clock!"
        },
        {
          type: "input",
          q: "Complete: 11:30 = ___ past ___",
          a: ["half past eleven", "Half past eleven"],
          tip: "HALF PAST eleven! 30 minutes after 11!",
          successtip: "✓ HALF PAST ELEVEN! 11:30! Common lunch time or late morning worldwide!"
        },
        {
          type: "mc",
          q: "What's another way to say 'quarter to six'?",
          options: ["5:45", "6:15", "5:15"],
          a: "5:45",
          tip: "QUARTER TO six = 5:45! 15 minutes before 6!",
          successtip: "✓ FIVE FORTY-FIVE! Quarter to 6 = 5:45! Two ways to express same time!"
        }
      ]
    },

    {
      id: "m6-u14-s6",
      title: "Set 6: More Time Expressions",
      category: "vocabulary",
      exercises: [
        {
          type: "mc",
          q: "How do you say 9:25?",
          options: ["Nine twenty-five", "Twenty-five past nine", "Both are correct"],
          a: "Both are correct",
          tip: "Two ways! Simple (nine twenty-five) or traditional (twenty-five past nine)!",
          successtip: "✓ BOTH WORK! Modern: 9:25, Traditional: twenty-five past nine! Choose preference!"
        },
        {
          type: "order",
          q: "Traduce: 3:50 (ten to four)",
          words: ["ten", "to", "four"],
          a: "ten to four",
          tip: "TEN TO four! 10 minutes before 4!",
          successtip: "✓ TEN TO FOUR! 3:50! Traditional way counts minutes TO next hour!"
        },
        {
          type: "input",
          q: "How do you say 9:27? nine ___ seven",
          a: ["twenty-seven", "twenty seven"],
          tip: "NINE TWENTY-SEVEN! Just say the numbers!",
          successtip: "✓ PRECISE TIME! For exact minutes, just say numbers! Nine twenty-seven!"
        },
        {
          type: "mc",
          q: "How do you say 'twenty-seven minutes past nine'?",
          options: ["9:27", "8:27", "9:37"],
          a: "9:27",
          tip: "Twenty-seven PAST nine = 27 minutes after 9 = 9:27!",
          successtip: "✓ NINE TWENTY-SEVEN! PAST means after! Traditional British style more formal!"
        },
        {
          type: "match",
          q: "Match the time expression:",
          pairs: [
            { left: "10 to 4", right: "3:50" },
            { left: "25 past 6", right: "6:25" },
            { left: "5 past 8", right: "8:05" }
          ],
          a: "completed",
          successtip: "✓ PAST & TO! PAST = after, TO = before! Pattern consistent for all times!"
        },
        {
          type: "order",
          q: "Traduce: Son las 12:00 (mediodía)",
          words: ["It's", "twelve", "o'clock", "noon"],
          a: "It's twelve o'clock noon",
          tip: "NOON = mediodía! Also just 'It's noon' works!",
          successtip: "✓ NOON! 12:00 midday! MIDNIGHT = 12:00 at night! Special words for 12!"
        },
        {
          type: "mc",
          q: "The train leaves at 3:51. How do you say this precisely?",
          options: ["Three fifty-one", "Fifty-one past three", "Nine to four"],
          a: "Three fifty-one",
          tip: "THREE FIFTY-ONE! Most natural for precise odd minutes!",
          successtip: "✓ THREE FIFTY-ONE! Precise times use simple number format! Schedules, appointments!"
        },
        {
          type: "input",
          q: "Complete: It's 6:40 = It's twenty ___ ___",
          a: ["to seven", "To seven", "to Seven"],
          tip: "Twenty TO seven! 20 minutes before 7!",
          successtip: "✓ TWENTY TO SEVEN! 6:40! Traditional way counts down TO next hour!"
        },
        {
          type: "mc",
          q: "Which is correct for schedules/timetables?",
          options: ["Half past three", "Three thirty", "Both work"],
          a: "Both work",
          tip: "Both acceptable! 'Three thirty' more common in schedules!",
          successtip: "✓ BOTH WORK! Schedules often use simple format (3:30), speech uses both!"
        },
        {
          type: "input",
          q: "How do you say 1:15? ___ past ___",
          a: ["quarter past one", "Quarter past one", "a quarter past one", "A quarter past one"],
          tip: "QUARTER PAST one! 15 minutes after 1!",
          successtip: "✓ QUARTER PAST ONE! 1:15! Pattern same for all hours with :15!"
        }
      ]
    },

    // ============================================
    // SETS 7-8: 24-HOUR CLOCK & PRECISE TIMES
    // ============================================
    {
      id: "m6-u14-s7",
      title: "Set 7: 24-Hour Clock",
      category: "vocabulary",
      exercises: [
        {
          type: "mc",
          q: "How do you say 0700 (24-hour clock)?",
          options: ["Seven hundred", "Oh seven hundred", "Zero seven hundred"],
          a: "Oh seven hundred",
          tip: "OH seven hundred! Use OH for zero! Military/transport time!",
          successtip: "✓ OH SEVEN HUNDRED! 24-hour clock common in military, trains, flights worldwide!"
        },
        {
          type: "order",
          q: "How to say 1626 (24-hour format)?",
          words: ["sixteen", "twenty-six"],
          a: "sixteen twenty-six",
          tip: "SIXTEEN TWENTY-SIX! Say pairs of numbers!",
          successtip: "✓ SIXTEEN TWENTY-SIX! 24-hour format splits into pairs! 16:26 = 4:26 PM!"
        },
        {
          type: "input",
          q: "0843 = oh eight ___-three",
          a: ["forty", "Forty"],
          tip: "OH EIGHT FORTY-THREE! Morning time!",
          successtip: "✓ OH EIGHT FORTY-THREE! 8:43 AM! Precise time format for schedules!"
        },
        {
          type: "mc",
          q: "1807 in 24-hour clock is what in 12-hour clock?",
          options: ["6:07 AM", "6:07 PM", "8:07 PM"],
          a: "6:07 PM",
          tip: "18:07 = 6:07 PM! Subtract 12 from hours over 12!",
          successtip: "✓ SIX OH SEVEN PM! 1807 = 18:07 = 6:07 PM! Military time minus 12 for PM!"
        },
        {
          type: "match",
          q: "Match 24-hour to 12-hour clock:",
          pairs: [
            { left: "1400", right: "2:00 PM" },
            { left: "2300", right: "11:00 PM" },
            { left: "0600", right: "6:00 AM" }
          ],
          a: "completed",
          successtip: "✓ CONVERSION! 24-hour removes AM/PM confusion! Used globally for transport!"
        },
        {
          type: "order",
          q: "Traduce: 0900 (military time)",
          words: ["oh", "nine", "hundred"],
          a: "oh nine hundred",
          tip: "OH NINE HUNDRED! Exact hours say 'hundred'!",
          successtip: "✓ OH NINE HUNDRED! 9:00 AM! HUNDRED only for exact hours in 24-hour format!"
        },
        {
          type: "mc",
          q: "The flight departs at 2145. How do you say this?",
          options: ["Twenty-one forty-five", "Nine forty-five PM", "Both are correct"],
          a: "Both are correct",
          tip: "Both work! 24-hour (twenty-one forty-five) or 12-hour (nine forty-five PM)!",
          successtip: "✓ BOTH! Transport often uses 24-hour, but converting to PM acceptable!"
        },
        {
          type: "input",
          q: "Complete: 1555 = fifteen ___-five",
          a: ["fifty", "Fifty"],
          tip: "FIFTEEN FIFTY-FIVE! Afternoon time!",
          successtip: "✓ FIFTEEN FIFTY-FIVE! 3:55 PM! Very precise time for schedules!"
        },
        {
          type: "mc",
          q: "ERROR COMÚN: The train leaves at the 1630. ¿Correcto?",
          options: ["Yes, correct", "No, at 1630", "No, in 1630"],
          a: "No, at 1630",
          tip: "No THE! AT 1630! No article with times!",
          successtip: "✓ AT! Use AT with times! At 1630, at 9 PM, at noon! No THE!"
        },
        {
          type: "input",
          q: "What's 1200 in 12-hour format? ___ noon or ___ PM",
          a: ["twelve", "Twelve", "12"],
          tip: "TWELVE noon or TWELVE PM! Midday!",
          successtip: "✓ TWELVE NOON! 1200 = noon = 12:00 PM! Midpoint of 24-hour day!"
        }
      ]
    },

    {
      id: "m6-u14-s8",
      title: "Set 8: Appointments & Schedules",
      category: "integration",
      exercises: [
        {
          type: "mc",
          q: "Your appointment is at 10:30 AM. Another way to say this?",
          options: ["At half past ten in the morning", "At ten and thirty AM", "At ten hours thirty"],
          a: "At half past ten in the morning",
          tip: "AT half past ten in the morning! Traditional style!",
          successtip: "✓ APPOINTMENTS! Medical, business, personal all use precise times! Be punctual!"
        },
        {
          type: "order",
          q: "Traduce: La reunión es a las 2:15 PM",
          words: ["The", "meeting", "is", "at", "quarter", "past", "two", "PM"],
          a: "The meeting is at quarter past two PM",
          tip: "AT quarter past two PM! Formal meeting time!",
          successtip: "✓ MEETINGS! Business worldwide uses precise times! Quarter past common!"
        },
        {
          type: "input",
          q: "Complete: The train arrives ___ 1845. (preposition)",
          a: ["at", "At"],
          tip: "AT 1845! Use AT with specific times!",
          successtip: "✓ AT! Preposition for specific times! AT 3 PM, AT noon, AT 1845!"
        },
        {
          type: "mc",
          q: "The store opens at 9:00 and closes at 5:30. What time format is this?",
          options: ["24-hour", "12-hour", "Military time"],
          a: "12-hour",
          tip: "12-HOUR! Business hours often use simple 12-hour AM/PM!",
          successtip: "✓ BUSINESS HOURS! 9-5 or 9-5:30 common worldwide! 12-hour for retail!"
        },
        {
          type: "match",
          q: "Match the situation to time format:",
          pairs: [
            { left: "Train schedule", right: "24-hour clock" },
            { left: "Casual meeting", right: "Half past two" },
            { left: "Flight departure", right: "0730" }
          ],
          a: "completed",
          successtip: "✓ CONTEXT MATTERS! Formal = 24-hour, Casual = traditional, Travel = varies!"
        },
        {
          type: "order",
          q: "Traduce: El vuelo sale a las 7:25 AM",
          words: ["The", "flight", "departs", "at", "seven", "twenty-five", "AM"],
          a: "The flight departs at seven twenty-five AM",
          tip: "DEPARTS = sale! SEVEN TWENTY-FIVE AM precise!",
          successtip: "✓ FLIGHT TIMES! Airlines use precise times! Always arrive early at airport!"
        },
        {
          type: "mc",
          q: "Your dentist appointment is at 1430. What time is this?",
          options: ["2:30 PM", "4:30 PM", "2:30 AM"],
          a: "2:30 PM",
          tip: "1430 = 14:30 = 2:30 PM! Subtract 12!",
          successtip: "✓ TWO THIRTY PM! Medical offices often use 24-hour internally! Know conversion!"
        },
        {
          type: "input",
          q: "Complete: The class starts ___ 8:15 and ends ___ 9:45.",
          a: ["at, at", "At, at", "at, At", "At, At"],
          tip: "AT both times! Use AT for specific times!",
          successtip: "✓ AT...AT! School schedules worldwide! 90-minute class common length!"
        },
        {
          type: "mc",
          q: "The movie starts at quarter to eight. What time?",
          options: ["7:45", "8:15", "7:15"],
          a: "7:45",
          tip: "QUARTER TO eight = 7:45! 15 minutes before 8!",
          successtip: "✓ SEVEN FORTY-FIVE! Movie times important! Arrive early for trailers!"
        },
        {
          type: "input",
          q: "Complete: I'll call you ___ noon tomorrow. (preposition)",
          a: ["at", "At"],
          tip: "AT noon! Always AT with specific times!",
          successtip: "✓ AT NOON! 12:00 PM! Midday! Common time for lunch meetings worldwide!"
        }
      ]
    },

    // ============================================
    // SETS 9-10: DATES (US vs UK)
    // ============================================
    {
      id: "m6-u14-s9",
      title: "Set 9: Dates - US Format",
      category: "vocabulary",
      exercises: [
        {
          type: "mc",
          q: "How do Americans write March 10, 2004?",
          options: ["10/03/2004", "03/10/2004", "2004/03/10"],
          a: "03/10/2004",
          tip: "US format: MONTH/DAY/YEAR! 03/10/2004!",
          successtip: "✓ US FORMAT! Month first! 03/10/2004 = March 10, 2004! Different from most countries!"
        },
        {
          type: "order",
          q: "How to say March 10, 2004 in US English?",
          words: ["March", "tenth", "two", "thousand", "and", "four"],
          a: "March tenth two thousand and four",
          tip: "US: MARCH TENTH! Month + ordinal day!",
          successtip: "✓ MARCH TENTH! US says month first, then ordinal number! March tenth, April fifth!"
        },
        {
          type: "input",
          q: "Write July 4, 2020 in US number format: __/__/____",
          a: ["07/04/2020", "7/4/2020"],
          tip: "MONTH/DAY/YEAR! July = 07 or 7!",
          successtip: "✓ JULY FOURTH! 07/04/2020! US Independence Day! Famous date worldwide!"
        },
        {
          type: "mc",
          q: "How do you SAY the date 12/25/2023 in US English?",
          options: ["December twenty-five, two thousand twenty-three", "Twenty-five December, two thousand twenty-three", "The twenty-fifth of December"],
          a: "December twenty-five, two thousand twenty-three",
          tip: "US: MONTH first! December twenty-five!",
          successtip: "✓ DECEMBER TWENTY-FIVE! Christmas Day! US format month first when speaking!"
        },
        {
          type: "match",
          q: "Match US written date to spoken:",
          pairs: [
            { left: "01/01/2025", right: "January first, two thousand twenty-five" },
            { left: "07/04/2024", right: "July fourth, two thousand twenty-four" },
            { left: "12/31/2023", right: "December thirty-first, two thousand twenty-three" }
          ],
          a: "completed",
          successtip: "✓ US DATES! Month first always! Important for understanding American documents!"
        },
        {
          type: "order",
          q: "Traduce: 5 de mayo de 2024 (US format spoken)",
          words: ["May", "fifth", "two", "thousand", "twenty-four"],
          a: "May fifth two thousand twenty-four",
          tip: "US: MAY FIFTH! Month + ordinal!",
          successtip: "✓ MAY FIFTH! Cinco de Mayo! Famous date! US format month first!"
        },
        {
          type: "mc",
          q: "ERROR COMÚN: 03/10/2004 means October 3rd in US. ¿Correcto?",
          options: ["Yes, correct", "No, March 10th", "No, October 30th"],
          a: "No, March 10th",
          tip: "US format! 03 = MARCH (month)! 10 = day!",
          successtip: "✓ MARCH TENTH! Common confusion! US puts MONTH first, not day!"
        },
        {
          type: "input",
          q: "Complete: August 15, 2023 = __/15/2023 (US format)",
          a: ["08", "8"],
          tip: "August = 08! Month first in US!",
          successtip: "✓ AUGUST! 08/15/2023! Mid-summer in Northern Hemisphere!"
        },
        {
          type: "mc",
          q: "How do you say 2020 in dates?",
          options: ["Twenty twenty", "Two thousand and twenty", "Both are correct"],
          a: "Both are correct",
          tip: "Both ways work! Twenty twenty shorter!",
          successtip: "✓ BOTH! Years flexible! 2020 = twenty twenty OR two thousand twenty! Choose preference!"
        },
        {
          type: "input",
          q: "Complete: September 11, 2001 in US = __/11/2001",
          a: ["09", "9"],
          tip: "September = 09! Important historical date!",
          successtip: "✓ SEPTEMBER ELEVENTH! 9/11! Historic date worldwide! Always remembered!"
        }
      ]
    },

    {
      id: "m6-u14-s10",
      title: "Set 10: Dates - UK Format & Integration",
      category: "vocabulary",
      exercises: [
        {
          type: "mc",
          q: "How do British people write 10 March 2004?",
          options: ["10/03/2004", "03/10/2004", "2004/03/10"],
          a: "10/03/2004",
          tip: "UK format: DAY/MONTH/YEAR! 10/03/2004!",
          successtip: "✓ UK FORMAT! Day first! 10/03/2004 = 10 March 2004! Used in most countries!"
        },
        {
          type: "order",
          q: "How to say 10 March 2004 in UK English?",
          words: ["the", "tenth", "of", "March", "two", "thousand", "and", "four"],
          a: "the tenth of March two thousand and four",
          tip: "UK: THE TENTH OF March! Day + OF + month!",
          successtip: "✓ THE TENTH OF MARCH! UK uses 'the' and 'of'! More formal structure!"
        },
        {
          type: "input",
          q: "Write 25 December 2023 in UK number format: __/__/____",
          a: ["25/12/2023"],
          tip: "DAY/MONTH/YEAR! 25 = day, 12 = December!",
          successtip: "✓ CHRISTMAS! 25/12/2023! UK format day first! Used across Europe!"
        },
        {
          type: "mc",
          q: "DIFFERENCE: 06/04/2024 in US vs UK?",
          options: ["Same date", "Different dates", "Both formats don't exist"],
          a: "Different dates",
          tip: "DIFFERENT! US = June 4th, UK = 6th April!",
          successtip: "✓ HUGE DIFFERENCE! 06/04/2024: US = June 4, UK = April 6! Always clarify format!"
        },
        {
          type: "match",
          q: "Match UK written date to spoken:",
          pairs: [
            { left: "01/01/2025", right: "the first of January" },
            { left: "14/02/2024", right: "the fourteenth of February" },
            { left: "31/12/2023", right: "the thirty-first of December" }
          ],
          a: "completed",
          successtip: "✓ UK DATES! THE...OF pattern! Day first! Used in UK, Australia, most of world!"
        },
        {
          type: "order",
          q: "Traduce: 1 de enero de 2025 (UK format spoken)",
          words: ["the", "first", "of", "January", "two", "thousand", "twenty-five"],
          a: "the first of January two thousand twenty-five",
          tip: "UK: THE FIRST OF January! Formal structure!",
          successtip: "✓ NEW YEAR'S DAY! The first of January! UK format more formal with 'the' and 'of'!"
        },
        {
          type: "mc",
          q: "Which is more common internationally?",
          options: ["US format (MM/DD/YYYY)", "UK format (DD/MM/YYYY)", "Both equally"],
          a: "UK format (DD/MM/YYYY)",
          tip: "UK/DAY FIRST used by most countries! US unique!",
          successtip: "✓ UK FORMAT! Day first used worldwide! US month first is exception! Know both!"
        },
        {
          type: "input",
          q: "Complete: 4th July 2024 in UK written = 04/__/2024",
          a: ["07", "7"],
          tip: "Day first! July = 07! UK format!",
          successtip: "✓ FOURTH OF JULY! 04/07/2024 UK format! Same as US Independence Day but written differently!"
        },
        {
          type: "mc",
          q: "ERROR COMÚN: Today is the March tenth. ¿Correcto en UK?",
          options: ["Yes, correct", "No, the tenth of March", "No, March the tenth"],
          a: "No, the tenth of March",
          tip: "UK needs OF! THE tenth OF March!",
          successtip: "✓ OF! UK structure needs 'of': the tenth OF March! US doesn't need 'of'!"
        },
        {
          type: "input",
          q: "INTEGRATION! My birthday is ___ 15, 2000 (US) = ___ 15th ___ ___, 2000 (UK spoken)",
          a: ["any month accepted as valid response"],
          tip: "Practice both formats! US: Month 15 / UK: the 15th of Month!",
          successtip: "✓ DATES MASTERED! 📅 You know both US and UK formats! Critical for international communication!"
        }
      ]
    }
  ],

  // ============================================
  // READING SET (REDUCIDO)
  // ============================================
  readingSet: {
    id: "m6-u14-reading",
    title: "Set 11: Reading Practice - Sports Statistics",
    category: "reading",
    passage: `The Athletics Championship Results

The annual Athletics Championship took place on Saturday, August 15th, 2024. The event started at 9:30 AM and finished at 6:45 PM. Over 5,000 spectators attended - that's 75% capacity of the stadium!

In the 100m race, Maria Santos won with a time of 10.89 seconds. She broke the previous record by 0.15 seconds! The men's race was even faster - Kevin Park finished in 9.97 seconds.

The high jump competition was exciting. Three athletes cleared 2.35 meters, but Emma Wilson won by jumping 2.40 meters on her second attempt. That's approximately three quarters of the way to breaking the world record of 3.20 meters.

Statistics show that 60% of the athletes were under 25 years old. The championship will return next year on the third Saturday of August at half past nine in the morning!`,
    questions: [
      {
        type: "mc",
        q: "When did the championship take place?",
        options: ["August 15th", "August 5th", "July 15th"],
        a: "August 15th",
        tip: "Check the date in the first paragraph!"
      },
      {
        type: "input",
        q: "What time did the event start? (format: __:__ AM)",
        a: ["9:30 AM", "9:30 am", "930 AM", "nine thirty AM"],
        tip: "First paragraph mentions start time!"
      },
      {
        type: "mc",
        q: "What percentage of the stadium was full?",
        options: ["50%", "75%", "100%"],
        a: "75%",
        tip: "Look for the percentage in first paragraph!"
      },
      {
        type: "input",
        q: "Maria Santos' time: 10.___ seconds",
        a: ["89"],
        tip: "Decimal time! Check women's 100m result!"
      },
      {
        type: "mc",
        q: "By how much did Maria break the record?",
        options: ["0.15 seconds", "1.5 seconds", "0.51 seconds"],
        a: "0.15 seconds",
        tip: "Decimal difference mentioned!"
      },
      {
        type: "input",
        q: "Kevin Park's time: 9.___ seconds",
        a: ["97"],
        tip: "Men's race result - decimal number!"
      },
      {
        type: "mc",
        q: "Emma Wilson jumped ___ meters.",
        options: ["2.35", "2.40", "3.20"],
        a: "2.40",
        tip: "Winning high jump height!"
      },
      {
        type: "mc",
        q: "What fraction describes progress toward world record?",
        options: ["Three quarters", "One half", "Two thirds"],
        a: "Three quarters",
        tip: "Text mentions fraction for record progress!"
      },
      {
        type: "mc",
        q: "What percentage of athletes were under 25?",
        options: ["50%", "60%", "75%"],
        a: "60%",
        tip: "Statistics in last paragraph!"
      },
      {
        type: "input",
        q: "Next year's start time: ___ past ___ (traditional way)",
        a: ["half past nine", "Half past nine", "half past 9"],
        tip: "Last sentence mentions next year's time!"
      }
    ]
  },

  // ============================================
  // REVIEW SET
  // ============================================
  reviewSet: {
    id: "m6-u14-review",
    title: "Set 12: Review - Numbers, Times & Dates",
    category: "review",
    exercises: [
      {
        type: "mc",
        q: "How do you say 1/2?",
        options: ["One two", "A half", "One second"],
        a: "A half",
        tip: "Special word! A HALF!",
        successtip: "✓ FRACTIONS! Half, quarter, third - most common in daily life!"
      },
      {
        type: "order",
        q: "Traduce: 3.97 (three point nine seven)",
        words: ["three", "point", "nine", "seven"],
        a: "three point nine seven",
        tip: "Each digit SEPARATE after point!",
        successtip: "✓ DECIMALS! Remember: digit by digit, never group after point!"
      },
      {
        type: "mc",
        q: "ERROR COMÚN: Three point ninety-seven. ¿Correcto?",
        options: ["Yes, correct", "No, three point nine seven", "Both work"],
        a: "No, three point nine seven",
        tip: "SEPARATE digits! Not 'ninety-seven'!",
        successtip: "✓ SEPARATE! Common error! 3.97 = three point nine seven, NOT ninety-seven!"
      },
      {
        type: "input",
        q: "Complete: 55% = fifty-five ___",
        a: ["percent", "Percent", "per cent", "Per cent"],
        tip: "% = PERCENT!",
        successtip: "✓ PERCENTAGES! Essential for statistics, discounts, news worldwide!"
      },
      {
        type: "mc",
        q: "How do you say 9:30?",
        options: ["Nine and thirty", "Half past nine", "Nine hours thirty"],
        a: "Half past nine",
        tip: "Traditional way! HALF PAST!",
        successtip: "✓ TIME! Two ways: 9:30 or half past nine! Know both!"
      },
      {
        type: "order",
        q: "Traduce: Son las 3:45 (quarter to four)",
        words: ["It's", "quarter", "to", "four"],
        a: "It's quarter to four",
        tip: "IT'S (not they are)! QUARTER TO!",
        successtip: "✓ IT'S! Always IT'S for time, never 'they are' like Spanish!"
      },
      {
        type: "match",
        q: "Match the ERROR to the CORRECTION:",
        pairs: [
          { left: "The three o'clock", right: "Three o'clock" },
          { left: "At the 1630", right: "At 1630" },
          { left: "Three point ninety-seven", right: "Three point nine seven" }
        ],
        a: "completed",
        tip: "Common errors review!",
        successtip: "✓ ERROR CORRECTION! No THE with times/numbers! Decimals separate!"
      },
      {
        type: "mc",
        q: "1630 in 24-hour clock = ___ in 12-hour?",
        options: ["4:30 AM", "4:30 PM", "6:30 PM"],
        a: "4:30 PM",
        tip: "Subtract 12! 16 - 12 = 4 PM!",
        successtip: "✓ CONVERSION! Essential skill for travel, transport worldwide!"
      },
      {
        type: "input",
        q: "Complete: 03/10/2024 in US format = ___ 10th (month name)",
        a: ["March", "march"],
        tip: "US: MONTH first! 03 = March!",
        successtip: "✓ US DATES! Month first! Different from most countries!"
      },
      {
        type: "order",
        q: "CELEBRATION! You mastered numbers, times, and dates! Say: I can understand statistics and schedules now!",
        words: ["I", "can", "understand", "statistics", "and", "schedules", "now"],
        a: "I can understand statistics and schedules now",
        tip: "You've learned fractions, decimals, percentages, times, and dates!",
        successtip: "✓ UNIT COMPLETE! 🎉📊🕐 You mastered numbers, times, and dates! Now you can understand sports statistics, read schedules, make appointments, and communicate precisely worldwide! You know both US and UK formats! Next: Making conversation and question tags! ¡Excelente progreso!"
      }
    ]
  }
};
